import { SagaIterator } from 'redux-saga';
import { call, select, takeEvery } from 'redux-saga/effects';
import {
  AngebotsId,
  BeginnAblaufGeaendert,
  GlobalActions,
  GlobalPut,
  UpdateBeginnAblaufAction,
  UpdateBeginnAblaufPayload,
} from '@ps20-frontend/shop-integration-redux';
import dayjs, { Dayjs } from 'dayjs';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '../localStore';
import { Produkt } from '../model';
import { CheckoutAction } from '../checkout.actions';
import { selectProdukt } from '../selector/produkt.selector';
import { selectDisableHauptfaelligkeitInput } from '../selector/beginnAblaufdatum.selector';
import { selectIsMindestvertragslaufzeitActive } from '../selector/mindestvertragslaufzeit.selector';
import {
  selectVorvertragAblaufDatum,
  selectVorvertragLaufzeit,
} from '../selector/vorvertragLaufzeit.selector';
import { calculateMindestlaufzeitAblauf } from './mindestvertragslaufzeit.util';

export const LAUFZEIT_3_JAHRE = 3;
export const LAUFZEIT_2_JAHRE = 2;
export const LAUFZEIT_1_JAHR = 1;

export function* watchUpdateBeginnAblauf(): SagaIterator {
  yield takeEvery(
    GlobalActions.UPDATE_BEGINN_ABLAUF,
    safeSaga(calculateNewBeginnAblauf, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    GlobalActions.HAUPTFAELLIGKEIT_CHANGED,
    safeSaga(hauptfaelligkeitChanged, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    GlobalActions.HAUPTFAELLIGKEIT_JHP_CHANGED,
    safeSaga(hauptfaelligkeitJHPChanged, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    GlobalActions.UPDATE_BEGINN_ABLAUF_OF_LOADED_STATE,
    safeSaga(updateBeginnABlaufAfterLoadAzam, App.SHOP_CHECKOUT),
  );
}

export function* updateBeginnABlaufAfterLoadAzam(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const appState: IAppState = yield select();
  if (selectDisableHauptfaelligkeitInput(appState)) {
    yield call(globalPut.put, new GlobalActions().initialHauptfaelligkeitRequested());
  } else if (appState?.hauptfaelligkeit) {
    yield call(
      globalPut.put,
      new GlobalActions().hauptfaelligkeitChanged(appState.hauptfaelligkeit),
    );
  }
}

export function* calculateNewBeginnAblauf(action: UpdateBeginnAblaufAction): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const appState: IAppState = yield select();
  const updateBeginnAblaufPayloads = yield call(generateBeginnAblaufPayloads, action.payload);

  yield call(
    globalPut.put,
    new GlobalActions().updateBeginnAblaufOfProdukte(updateBeginnAblaufPayloads),
  );

  const payloadProdukt = selectProdukt(appState, action.payload);
  if (payloadProdukt?.beibehalten) {
    yield call(globalPut.put, new GlobalActions().initialHauptfaelligkeitRequested());
  } else if (appState?.hauptfaelligkeit) {
    yield call(
      globalPut.put,
      new GlobalActions().hauptfaelligkeitChanged(appState.hauptfaelligkeit),
    );
  }
}

function* generateBeginnAblaufPayloads(actionPayload: UpdateBeginnAblaufPayload): SagaIterator {
  const appState: IAppState = yield select();
  const produkte = appState.produkte;

  let beginn = actionPayload.beginn
    ? actionPayload.beginn
    : dayjs().add(1, 'd').format(DATEFORMAT_LOCAL_STATE);
  if (actionPayload.appId === App.JHP && !actionPayload.beginn) {
    beginn = calculateNextAprilFirst(beginn);
  }

  if (!actionPayload.beginn) {
    const produkt = produkte.find(
      p => p.appId === actionPayload.appId && p.angebotsId === actionPayload.angebotsId
    );

    if (produkt?.beginn) {
      const currentBeginn = dayjs(produkt.beginn, DATEFORMAT_LOCAL_STATE);
      const calculatedBeginn = dayjs(beginn, DATEFORMAT_LOCAL_STATE);
      const tomorrow = dayjs().add(1, 'd').startOf('day');

      if (currentBeginn.isAfter(tomorrow) && currentBeginn.isAfter(calculatedBeginn)) {
        beginn = produkt.beginn;
      }
    }
  }

  const payloads = updateProdukteWithSpecificBeginnDatum(produkte, actionPayload, beginn);
  return overrideAblaufWithMindestlaufzeit(payloads, produkte, appState, actionPayload);
}

function overrideAblaufWithMindestlaufzeit(
  payloads: BeginnAblaufGeaendert[],
  produkte: Array<Produkt>,
  appState: IAppState,
  actionPayload: UpdateBeginnAblaufPayload,
): BeginnAblaufGeaendert[] {
  if (!selectIsMindestvertragslaufzeitActive(appState) || !appState.hauptfaelligkeit) {
    return payloads;
  }
  const hf = dayjs(appState.hauptfaelligkeit, DATEFORMAT_SHORT_ISO);
  if (!hf.isValid()) {
    return payloads;
  }
  const hfMMDD = hf.format('DD.MM.');
  return payloads.map(payload => {
    if (payload.appId === App.JHP) {
      return payload;
    }
    const produkt = produkte.find(
      p => p.appId === payload.appId && p.angebotsId === payload.angebotsId,
    );
    if (!produkt || !(produkt.vorvertragsnummer?.length > 0)) {
      return payload;
    }
    const beginnForCalc =
      payload.appId === actionPayload.appId && payload.angebotsId === actionPayload.angebotsId
        ? payload.beginn
        : produkt.beginn;
    const ablauf = calculateMindestlaufzeitAblauf(
      produkt,
      beginnForCalc,
      selectVorvertragLaufzeit(produkt),
      selectVorvertragAblaufDatum(produkt),
      hfMMDD,
    );
    return ablauf ? { ...payload, ablauf } : payload;
  });
}

export function* hauptfaelligkeitChanged(action: CheckoutAction<string>): SagaIterator {
  if (action.payload) {
    const appState: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();
    const mindestlaufzeitActive = selectIsMindestvertragslaufzeitActive(appState);

    const updateBeginnAblaufPayloads: BeginnAblaufGeaendert[] = appState.produkte
      .filter(produkt => produkt.appId !== App.JHP)
      .filter(
        produkt =>
          !(
            mindestlaufzeitActive &&
            produkt.vorvertragsnummer?.length > 0 &&
            selectVorvertragLaufzeit(produkt) !== undefined
          ),
      )
      .map(produkt => ({
        appId: produkt.appId,
        angebotsId: produkt.angebotsId,
        beginn: produkt.beginn,
        ablauf: calculateAblaufWithHauptfaelligkeit(produkt, action.payload),
      }));
    if (updateBeginnAblaufPayloads.length > 0) {
      yield call(
        globalPut.put,
        new GlobalActions().updateBeginnAblaufOfProdukte(updateBeginnAblaufPayloads),
      );
    }
  }
}

export function* hauptfaelligkeitJHPChanged(action: CheckoutAction<string>): SagaIterator {
  if (action.payload) {
    const appState: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();

    const updateBeginnAblaufPayloads: BeginnAblaufGeaendert[] = appState.produkte
      .filter(produkt => produkt.appId === App.JHP)
      .map(produkt => ({
        appId: produkt.appId,
        angebotsId: produkt.angebotsId,
        beginn: produkt.beginn,
        ablauf: calculateAblaufWithHauptfaelligkeit(produkt, action.payload),
      }));
    yield call(
      globalPut.put,
      new GlobalActions().updateBeginnAblaufOfProdukte(updateBeginnAblaufPayloads),
    );
  }
}

function getAllowedDurationRange(
  productBegin: Dayjs,
  laufzeit: number,
): { maxDuration: number; minDuration: number } {
  const maxDuration3Jahre = getDifferenceInYears(
    productBegin,
    productBegin.add(LAUFZEIT_3_JAHRE, 'year'),
  );
  const minDuration3Jahre = getDifferenceInYears(
    productBegin,
    productBegin.add(2, 'year').add(1, 'day'),
  );

  const maxDuration1Jahr = getDifferenceInYears(
    productBegin,
    productBegin.add(LAUFZEIT_2_JAHRE, 'year'),
  );
  const minDuration1Jahr = getDifferenceInYears(productBegin, productBegin.add(1, 'year'));

  const maxDuration = laufzeit === LAUFZEIT_1_JAHR ? maxDuration1Jahr : maxDuration3Jahre;
  const minDuration = laufzeit === LAUFZEIT_1_JAHR ? minDuration1Jahr : minDuration3Jahre;
  return { maxDuration, minDuration };
}

function logHauptfaelligkeitDurationError(
  produkt: Produkt,
  productBegin: Dayjs,
  productEndeWithHauptfaelligkeit: Dayjs,
): void {
  const formattedProductEndeWithHpf = dayjs(
    productEndeWithHauptfaelligkeit,
    DATEFORMAT_SHORT_ISO,
  ).format(DATEFORMAT_LOCAL_STATE);
  const formattedProductBeginn = dayjs(productBegin, DATEFORMAT_SHORT_ISO).format(
    DATEFORMAT_LOCAL_STATE,
  );
  const errorTextThreeYears =
    'Vertraglaufzeit mit Hauptfaelligkeit ist länger als 3 Jahre oder kurzer als 2 Jahre und 1 Tag. ' +
    'Beginndatum ist ' +
    formattedProductBeginn +
    ', Ablaufdatum ist ' +
    formattedProductEndeWithHpf;
  const errorTextOneYear =
    'Vertraglaufzeit mit Hauptfaelligkeit ist länger als 2 Jahre oder kurzer als 1 Jahr. ' +
    'Beginndatum ist ' +
    formattedProductBeginn +
    ', Ablaufdatum ist ' +
    formattedProductEndeWithHpf;
  PS20Logger.info(
    produkt.laufzeit === LAUFZEIT_1_JAHR ? errorTextOneYear : errorTextThreeYears,
    '',
    App.SHOP_CHECKOUT,
  );
}

function calculateAblaufWithHauptfaelligkeit(produkt: Produkt, hauptfaelligkeit: string): string {
  if (hauptfaelligkeit && produkt.laufzeit) {
    const productBegin = dayjs(produkt.beginn, DATEFORMAT_LOCAL_STATE);

    const { maxDuration, minDuration } = getAllowedDurationRange(productBegin, produkt.laufzeit);

    const productEndeWithHauptfaelligkeitYear =
      produkt.laufzeit === LAUFZEIT_1_JAHR
        ? getProductEndeWithHauptfaelligkeitForOneYear(hauptfaelligkeit, productBegin)
        : LAUFZEIT_3_JAHRE;

    let productEndeWithHauptfaelligkeit = dayjs(hauptfaelligkeit, DATEFORMAT_SHORT_ISO).year(
      productBegin.add(productEndeWithHauptfaelligkeitYear, 'year').year(),
    );

    if (getDifferenceInYears(productBegin, productEndeWithHauptfaelligkeit) > maxDuration) {
      productEndeWithHauptfaelligkeit = productEndeWithHauptfaelligkeit.subtract(1, 'year');
    }
    if (getDifferenceInYears(productBegin, productEndeWithHauptfaelligkeit) < minDuration) {
      productEndeWithHauptfaelligkeit = productEndeWithHauptfaelligkeit.add(1, 'year');
    }

    if (
      getDifferenceInYears(productBegin, productEndeWithHauptfaelligkeit) > maxDuration ||
      getDifferenceInYears(productBegin, productEndeWithHauptfaelligkeit) < minDuration
    ) {
      logHauptfaelligkeitDurationError(produkt, productBegin, productEndeWithHauptfaelligkeit);
    }
    return productEndeWithHauptfaelligkeit.format(DATEFORMAT_LOCAL_STATE);
  }
}

function getProductEndeWithHauptfaelligkeitForOneYear(
  hauptfaelligkeit: string,
  productBegin: Dayjs,
) {
  const formattedHauptfaelligkeit: Dayjs = dayjs(hauptfaelligkeit, DATEFORMAT_SHORT_ISO).year(
    productBegin.year(),
  );
  return formattedHauptfaelligkeit.isBefore(productBegin) ? LAUFZEIT_2_JAHRE : LAUFZEIT_1_JAHR;
}

function getDifferenceInYears(begin: Dayjs, end: Dayjs) {
  return end.diff(begin, 'year', true);
}

function updateProdukteWithSpecificBeginnDatum(
  produkte: Array<Produkt>,
  angebot: AngebotsId,
  beginn: string,
) {
  const aeltesterVersicherungsbeginn = getAeltestenVersicherungsbeginn(
    getAllBeginne(produkte, angebot, beginn),
  );

  return produkte.map(produkt => ({
    appId: produkt.appId,
    angebotsId: produkt.angebotsId,
    beginn: getBeginnDatumForProdukt(produkt, angebot, beginn),
    ablauf: aeltesterVersicherungsbeginn
      .add(produkt.laufzeit, 'year')
      .format(DATEFORMAT_LOCAL_STATE),
  }));
}

export function isVertragslaufzeitInAllenProdukten3Jahre(produkte: Array<Produkt>): boolean {
  return produkte.every(produkt => produkt.laufzeit === LAUFZEIT_3_JAHRE);
}

function getAllBeginne(produkte: Array<Produkt>, angebotId: AngebotsId, beginn: string): string[] {
  const result = [];
  for (const produkt of produkte) {
    result.push(getBeginnDatumForProdukt(produkt, angebotId, beginn));
  }
  return result;
}

export function getBeginnDatumForProdukt(
  produkt: Produkt,
  angebot: AngebotsId,
  beginn: string,
): string {
  if (produkt.appId === angebot.appId && produkt.angebotsId === angebot.angebotsId) {
    return beginn;
  } else {
    return produkt.beginn;
  }
}

export function getAeltestenVersicherungsbeginn(beginne: string[]) {
  const versicherungsbeginne = beginne.map(beginn => dayjs(beginn, DATEFORMAT_LOCAL_STATE));
  return dayjs.min(versicherungsbeginne);
}

export function calculateNextAprilFirst(dateString: string): string {
  const date = dayjs(dateString, DATEFORMAT_LOCAL_STATE);
  const yearOffset = date.month() < 3 || (date.month() === 3 && date.date() === 1) ? 0 : 1;
  return dayjs()
    .date(1)
    .month(3)
    .year(date.year())
    .add(yearOffset, 'year')
    .format(DATEFORMAT_LOCAL_STATE);
}
